import React, { useState, useMemo, useCallback } from "react";
import { useQuery } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { AccordionSet, Accordion, Badge, Button, Checkbox, Row, Col, Dropdown, DropdownMenu, MenuSection, MessageBanner, } from "@folio/stripes/components";
import { useIntl, FormattedMessage } from "react-intl";
import { PeriodicalLibraryStatusEnum, } from "../../types/Api";
import { checkSettingsOf, } from "../../types/periodicalCheck";
import { fetchVolumeMatrix, } from "../../api/periodicalIssueService";
import { ISSUE_MATRIX_KEY, } from "../../hooks/usePeriodicalVolumes";
import { formatVolumeAnchor, formatVolumeDate, volumeOptionLabel, } from "../../utils/volumeWindow";
import { PeriodicalKultSubjectGroupLine } from "./PeriodicalKultSubjectGroupLine";
import { IssueStatusDialog } from "./IssueStatusDialog";
import { PeriodicalCheckSettingsLink } from "./PeriodicalCheckSettingsLink";
import { AddSpecialIssueDialog } from "./AddSpecialIssueDialog";
import { AddIrregularIssueDialog } from "./AddIrregularIssueDialog";
import { AddRegularIssueDialog } from "./AddRegularIssueDialog";
import { GenerateVolumeDialog } from "./GenerateVolumeDialog";
import { VolumeLibraryRecordsOffer } from "./VolumeLibraryRecordsOffer";
import { IssueLayoutTier1 } from "./IssueLayoutTier1";
import { IssueLayoutTier2 } from "./IssueLayoutTier2";
import { IssueLayoutTier3 } from "./IssueLayoutTier3";
import { frequencyToTier } from "../../utils/issueLayoutTier";
import { isManualFrequency as isManualFrequencyCheck } from "../../utils/manualFrequencies";
import css from "./IssueMatrixPanel.module.css";
var EMPTY_ISSUES = [];
export var IssueMatrixPanel = function (_a) {
    var _b, _c, _d, _e, _f;
    var periodicalLibrary = _a.periodicalLibrary, volumeSelection = _a.volumeSelection, onChooseVolume = _a.onChooseVolume;
    var formatMessage = useIntl().formatMessage;
    var kyInstance = useOkapiKy();
    var _g = useState(false), definitionMode = _g[0], setDefinitionMode = _g[1];
    var _h = useState(null), generateDirection = _h[0], setGenerateDirection = _h[1];
    var _j = useState(null), dialogIssue = _j[0], setDialogIssue = _j[1];
    var _k = useState(false), specialIssueDialogOpen = _k[0], setSpecialIssueDialogOpen = _k[1];
    var _l = useState(false), irregularDialogOpen = _l[0], setIrregularDialogOpen = _l[1];
    var _m = useState(false), regularDialogOpen = _m[0], setRegularDialogOpen = _m[1];
    var isManualFrequency = isManualFrequencyCheck(periodicalLibrary.frequency);
    var settings = useMemo(function () { return checkSettingsOf(periodicalLibrary); }, [periodicalLibrary]);
    var volumes = volumeSelection.volumes, volumesLoading = volumeSelection.isLoading, volumesFailed = volumeSelection.isError, selectedVolume = volumeSelection.selectedVolume;
    var _o = useQuery([ISSUE_MATRIX_KEY, periodicalLibrary.id, selectedVolume === null || selectedVolume === void 0 ? void 0 : selectedVolume.id], function () {
        return fetchVolumeMatrix(kyInstance, periodicalLibrary.id, selectedVolume === null || selectedVolume === void 0 ? void 0 : selectedVolume.id);
    }, { enabled: !!selectedVolume, keepPreviousData: true }), matrixData = _o.data, matrixLoading = _o.isLoading, matrixFailed = _o.isError;
    var isLoading = !matrixFailed &&
        (matrixLoading ||
            (!!matrixData && matrixData.volume.id !== (selectedVolume === null || selectedVolume === void 0 ? void 0 : selectedVolume.id)));
    var currentMatrix = useMemo(function () {
        return !matrixFailed && matrixData && matrixData.volume.id === (selectedVolume === null || selectedVolume === void 0 ? void 0 : selectedVolume.id)
            ? matrixData
            : undefined;
    }, [matrixData, matrixFailed, selectedVolume]);
    var visibleIssues = (_b = currentMatrix === null || currentMatrix === void 0 ? void 0 : currentMatrix.issues) !== null && _b !== void 0 ? _b : EMPTY_ISSUES;
    var visibleMissingCount = (_c = currentMatrix === null || currentMatrix === void 0 ? void 0 : currentMatrix.missingCount) !== null && _c !== void 0 ? _c : 0;
    var libraryHasNoRecords = useMemo(function () {
        return visibleIssues.length > 0 &&
            visibleIssues.every(function (issue) { return !issue.depositRecordId; });
    }, [visibleIssues]);
    var tier = useMemo(function () { return frequencyToTier(periodicalLibrary.frequency); }, [periodicalLibrary.frequency]);
    var handleIssueClick = useCallback(function (issue) {
        setDialogIssue(issue);
    }, []);
    var handleDialogClose = useCallback(function () {
        setDialogIssue(null);
    }, []);
    var handleGenerated = useCallback(function (volume) { return onChooseVolume(volume.id); }, [onChooseVolume]);
    var handleOpenSpecialIssue = useCallback(function () { return setSpecialIssueDialogOpen(true); }, []);
    var handleCloseSpecialIssue = useCallback(function () { return setSpecialIssueDialogOpen(false); }, []);
    var handleOpenIrregular = useCallback(function () { return setIrregularDialogOpen(true); }, []);
    var handleCloseIrregular = useCallback(function () { return setIrregularDialogOpen(false); }, []);
    var handleOpenRegular = useCallback(function () { return setRegularDialogOpen(true); }, []);
    var handleCloseRegular = useCallback(function () { return setRegularDialogOpen(false); }, []);
    var handleOpenGeneratePrevious = useCallback(function () { return setGenerateDirection("previous"); }, []);
    var handleOpenGenerateNext = useCallback(function () { return setGenerateDirection("next"); }, []);
    var handleCloseGenerate = useCallback(function () { return setGenerateDirection(null); }, []);
    var handleToggleDefinitionMode = useCallback(function (e) {
        return setDefinitionMode(e.target.checked);
    }, []);
    var handleVolumeToggle = useCallback(function (_a) {
        var id = _a.id;
        return onChooseVolume(id.replace(/^volume-/, ""));
    }, [onChooseVolume]);
    var anchorLabel = formatVolumeAnchor(formatMessage, settings.volumeStartDay, settings.volumeStartMonth);
    var anchorMismatch = (_d = periodicalLibrary.volumeAnchorMismatch) !== null && _d !== void 0 ? _d : null;
    var proposedAnchor = useMemo(function () {
        return (anchorMismatch === null || anchorMismatch === void 0 ? void 0 : anchorMismatch.suggestedDay) && anchorMismatch.suggestedMonth
            ? {
                day: anchorMismatch.suggestedDay,
                month: anchorMismatch.suggestedMonth,
            }
            : null;
    }, [anchorMismatch]);
    var volumeAccordionLabel = useCallback(function (volume) {
        var described = volumeOptionLabel(volume, formatMessage);
        return volume.overlapsWith.length > 0
            ? formatMessage({ id: "ui-br-legaldeposits.periodicals.volume.overlapLabel" }, { volume: described, others: volume.overlapsWith.join(", ") })
            : described;
    }, [formatMessage]);
    var renderAddMenu = function (_a) {
        var onToggle = _a.onToggle;
        return (React.createElement(DropdownMenu, null,
            React.createElement(MenuSection, { id: "periodical-add-menu" },
                isManualFrequency && (React.createElement(Button, { buttonStyle: "dropdownItem", fullWidth: true, onClick: function () {
                        onToggle();
                        handleOpenRegular();
                    } },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.action.add-regular-issue" }))),
                React.createElement(Button, { buttonStyle: "dropdownItem", fullWidth: true, onClick: function () {
                        onToggle();
                        handleOpenIrregular();
                    } },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.action.add-irregular-issue" })),
                React.createElement(Button, { buttonStyle: "dropdownItem", fullWidth: true, onClick: function () {
                        onToggle();
                        handleOpenSpecialIssue();
                    } },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.action.add-special-issue" })))));
    };
    var addDropdown = (React.createElement(Dropdown, { label: formatMessage({
            id: "ui-br-legaldeposits.periodicals.action.add",
        }), renderMenu: renderAddMenu }));
    return (React.createElement("div", null,
        React.createElement("div", { className: css.infoSection },
            periodicalLibrary.periodicalActive === false && (React.createElement("div", { className: css.endedBadge },
                React.createElement(Badge, { color: "red" },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.status.ended" })))),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 6 },
                    React.createElement("strong", null,
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.field.issn" }),
                        ":"),
                    " ", (_e = periodicalLibrary.issn) !== null && _e !== void 0 ? _e : "—"),
                React.createElement(Col, { xs: 6 },
                    React.createElement("strong", null,
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.field.publisher" }),
                        ":"),
                    " ", (_f = periodicalLibrary.publisherName) !== null && _f !== void 0 ? _f : "—")),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 6 },
                    React.createElement("strong", null,
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.field.frequency" }),
                        ":"),
                    " ",
                    periodicalLibrary.frequency
                        ? formatMessage({
                            id: "ui-br-legaldeposits.periodicals.frequency.".concat(periodicalLibrary.frequency),
                        })
                        : "—"),
                React.createElement(Col, { xs: 6 },
                    React.createElement("strong", null,
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.field.numbering" }),
                        ":"),
                    " ",
                    formatMessage({
                        id: settings.continuousNumbering
                            ? "ui-br-legaldeposits.periodicals.numbering.continuous"
                            : "ui-br-legaldeposits.periodicals.numbering.perYear",
                    }))),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 6 },
                    React.createElement("strong", null,
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.field.volumeAnchor" }),
                        ":"),
                    " ",
                    anchorLabel,
                    " ",
                    React.createElement(PeriodicalCheckSettingsLink, { settings: settings, label: formatMessage({
                            id: "ui-br-legaldeposits.periodicals.volumeAnchor.change",
                        }) }))),
            anchorMismatch && (React.createElement(MessageBanner, { type: "warning" },
                React.createElement("p", { className: css.mismatchText }, formatMessage({ id: "ui-br-legaldeposits.periodicals.volumeAnchor.mismatch" }, {
                    anchor: anchorLabel,
                    volume: anchorMismatch.volumeLabel,
                    start: formatVolumeDate(anchorMismatch.startDate),
                })),
                React.createElement(PeriodicalCheckSettingsLink, { settings: settings, proposedAnchor: proposedAnchor, buttonStyle: "default", label: proposedAnchor
                        ? formatMessage({
                            id: "ui-br-legaldeposits.periodicals.volumeAnchor.mismatch.fix",
                        }, {
                            anchor: formatVolumeAnchor(formatMessage, proposedAnchor.day, proposedAnchor.month),
                        })
                        : formatMessage({
                            id: "ui-br-legaldeposits.periodicals.volumeAnchor.change",
                        }) }))),
            periodicalLibrary.titleId && (React.createElement(Row, null,
                React.createElement(Col, { xs: 12 },
                    React.createElement(PeriodicalKultSubjectGroupLine, { titleId: periodicalLibrary.titleId }))))),
        volumesLoading && React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.loading" }),
        volumesFailed && (React.createElement(MessageBanner, { type: "error" },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.volume.loadError" }))),
        !volumesLoading && !volumesFailed && volumes.length === 0 && (React.createElement("div", { className: css.emptyVolumes },
            React.createElement("p", null,
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.volume.none" })),
            React.createElement("div", { className: css.accordionHeader },
                React.createElement(Button, { marginBottom0: true, onClick: handleOpenGenerateNext },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.volume.generateFirst" })),
                addDropdown))),
        React.createElement(AccordionSet, null, volumes.map(function (volume) {
            var isSelected = volume.id === (selectedVolume === null || selectedVolume === void 0 ? void 0 : selectedVolume.id);
            return (React.createElement(Accordion, { key: volume.id, id: "volume-".concat(volume.id), label: volumeAccordionLabel(volume), open: isSelected, onToggle: handleVolumeToggle, displayWhenOpen: isSelected ? (React.createElement("div", { className: css.accordionHeader },
                    visibleMissingCount > 0 && (React.createElement(Badge, { color: "red" }, visibleMissingCount)),
                    React.createElement(Checkbox, { checked: definitionMode, onChange: handleToggleDefinitionMode, label: formatMessage({
                            id: "ui-br-legaldeposits.periodicals.action.toggle-definition-mode",
                        }) }),
                    addDropdown)) : undefined }, isSelected && (React.createElement(React.Fragment, null,
                volume.overlapsWith.length > 0 && (React.createElement(MessageBanner, { type: "warning" },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.volume.overlapHint", values: { others: volume.overlapsWith.join(", ") } }))),
                isLoading && (React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.loading" })),
                matrixFailed && (React.createElement(MessageBanner, { type: "error" },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.volume.matrixLoadError" }))),
                !isLoading &&
                    !matrixFailed &&
                    visibleIssues.length === 0 && (React.createElement("p", null,
                    React.createElement(FormattedMessage, { id: isManualFrequency
                            ? "ui-br-legaldeposits.periodicals.volume.emptyManual"
                            : "ui-br-legaldeposits.periodicals.volume.emptyScheduled" }))),
                !isLoading && libraryHasNoRecords && (React.createElement(VolumeLibraryRecordsOffer, { periodicalLibraryId: periodicalLibrary.id, volume: volume })),
                !isLoading && visibleIssues.length > 0 && (React.createElement("div", { className: "".concat(css.matrixContainer, " ").concat(definitionMode ? css.definitionMode : "") },
                    tier === 1 && (React.createElement(IssueLayoutTier1, { issues: visibleIssues, onIssueClick: handleIssueClick, onLinkedVolumeClick: onChooseVolume })),
                    tier === 2 && (React.createElement(IssueLayoutTier2, { issues: visibleIssues, onIssueClick: handleIssueClick, onLinkedVolumeClick: onChooseVolume })),
                    tier === 3 && (React.createElement(IssueLayoutTier3, { issues: visibleIssues, onIssueClick: handleIssueClick, onLinkedVolumeClick: onChooseVolume }))))))));
        })),
        volumes.length > 0 && (React.createElement("div", { className: css.ghostVolumeRow },
            React.createElement(Button, { buttonStyle: "default", marginBottom0: true, onClick: handleOpenGeneratePrevious },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.volume.generatePrevious" })))),
        React.createElement(GenerateVolumeDialog, { open: generateDirection !== null, periodicalLibraryId: periodicalLibrary.id, direction: generateDirection !== null && generateDirection !== void 0 ? generateDirection : "next", onClose: handleCloseGenerate, onGenerated: handleGenerated }),
        dialogIssue && selectedVolume && (React.createElement(IssueStatusDialog, { issue: dialogIssue, allIssues: visibleIssues, periodicalLibraryId: periodicalLibrary.id, subscribed: periodicalLibrary.status === PeriodicalLibraryStatusEnum.SUBSCRIBED, volume: selectedVolume, volumes: volumes, mode: definitionMode ? "definition" : "library", numberEditable: isManualFrequency, onClose: handleDialogClose })),
        React.createElement(AddSpecialIssueDialog, { periodicalLibraryId: periodicalLibrary.id, volumes: volumes, open: specialIssueDialogOpen, onClose: handleCloseSpecialIssue, onCreated: onChooseVolume }),
        React.createElement(AddIrregularIssueDialog, { periodicalLibraryId: periodicalLibrary.id, settings: settings, volumes: volumes, open: irregularDialogOpen, numberEditable: isManualFrequency, onClose: handleCloseIrregular, onCreated: onChooseVolume }),
        React.createElement(AddRegularIssueDialog, { periodicalLibraryId: periodicalLibrary.id, settings: settings, volumes: volumes, open: regularDialogOpen, numberEditable: isManualFrequency, onClose: handleCloseRegular, onCreated: onChooseVolume })));
};
